export type Badge =
  | "Le Grand Classique"
  | "Végétarien"
  | "Poulet Halal"
  | "Prestige"
  | "Signature Maison";

/** Clés des filtres rapides proposés au-dessus de la carte. */
export type Tag = "vegetarien" | "sansViande" | "prestige";

export type Dish = {
  name: string;
  description: string;
  price: string;
  /** Optionnel : un plat sans visuel juste n'affiche pas de dépliant photo. */
  photo?: string;
  alt?: string;
  badges?: Badge[];
  tags?: Tag[];
};

export type Category = {
  id: string;
  icon: string;
  label: string;
  short: string;
  tagline: string;
  dishes: Dish[];
};

export const FILTERS: { id: Tag; label: string }[] = [
  { id: "vegetarien", label: "Végétarien" },
  { id: "sansViande", label: "Sans viande" },
  { id: "prestige", label: "Truffes & Prestige" },
];

/** Construit une URL Unsplash. `auto=format` sert de l'AVIF/WebP quand c'est possible. */
export function photoUrl(id: string, w: number, h?: number, q = 72) {
  const crop = h ? `&h=${h}` : "";
  return `https://images.unsplash.com/${id}?auto=format&fit=crop&crop=entropy&w=${w}${crop}&q=${q}`;
}

export const CATEGORIES: Category[] = [
  {
    id: "rosse",
    icon: "🍕",
    label: "Pizzas Rouges",
    short: "Rosse",
    tagline:
      "Base sauce tomate parfumée aux herbes et mozzarella. Cuisson au four, à la commande.",
    dishes: [
      {
        name: "Margherita",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, basilic frais, origan et huile d'olive.",
        price: "12,80 €",
        photo: "photo-1598023696416-0193a0bcd302",
        alt: "Pizza Margherita au basilic frais",
        badges: ["Le Grand Classique"],
        tags: ["vegetarien", "sansViande"],
      },
      {
        name: "Regina",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, champignons, jambon de Paris.",
        price: "16,80 €",
        photo: "photo-1587085416963-22efba033dd5",
        alt: "Pizza Regina aux champignons et jambon de Paris",
      },
      {
        name: "Quattro Stagioni",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, jambon de Paris, 3 couleurs de poivrons marinés à l'huile d'olive, cœurs d'artichauts et champignons.",
        price: "17,80 €",
        photo: "photo-1571997478779-2adcbbe9ab2f",
        alt: "Pizza Quattro Stagioni aux artichauts et poivrons marinés",
      },
      {
        name: "Calzone",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, jambon de Paris, parmesan finement râpé, œuf frais.",
        price: "17,50 €",
        photo: "photo-1753656681797-3234c89d6d4d",
        alt: "Calzone soufflé doré sur une planche en bois",
      },
      {
        name: "Vegetariana",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, aubergines, champignons de Paris, oignons, courgettes grillées, brocoli, huile d'olive et origan.",
        price: "17,50 €",
        photo: "photo-1651307435894-ead7a594b534",
        alt: "Pizza Vegetariana garnie de brocoli et de légumes grillés",
        badges: ["Végétarien"],
        tags: ["vegetarien", "sansViande"],
      },
      {
        name: "Pizza Alla Parmigiana",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, aubergines grillées, tomates séchées, huile d'olive, basilic frais et sauce pesto.",
        price: "18,90 €",
        photo: "photo-1604068549290-dea0e4a305ca",
        alt: "Pizza alla Parmigiana aux aubergines grillées et pesto",
        tags: ["vegetarien", "sansViande"],
      },
      {
        name: "Palermo",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, olives, œuf frais, chorizo, 3 couleurs de poivrons marinés.",
        price: "16,80 €",
        photo: "photo-1625401514458-6c8a5e7d55da",
        alt: "Pizza Palermo au chorizo et poivrons marinés",
      },
      {
        name: "Campagnola",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, poitrine fumée et œuf frais.",
        price: "16,90 €",
        photo: "photo-1627626775846-122b778965ae",
        alt: "Pizza Campagnola à la poitrine fumée et à l'œuf",
      },
      {
        name: "Andrellini",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, fromage de chèvre, jambon de Paris, 3 couleurs de poivrons marinés à l'huile d'olive, ail frais et oignons doux.",
        price: "17,90 €",
        photo: "photo-1617470703128-26a0fc9af10f",
        alt: "Pizza Andrellini au chèvre, poivrons et oignons doux",
      },
      {
        name: "Campione",
        description:
          "Sauce tomate parfumée aux herbes, viande de bœuf hachée lentement mijotée maison, mozzarella, champignons de Paris et parmesan râpé.",
        price: "16,90 €",
        photo: "photo-1608562099373-69dd6b715cd9",
        alt: "Pizza Campione au bœuf mijoté maison et parmesan",
      },
      {
        name: "Cinque Formaggi",
        description:
          "Sauce tomate parfumée aux herbes et 5 fromages sélectionnés : mozzarella, chèvre, brie, emmental, gorgonzola.",
        price: "17,80 €",
        photo: "photo-1574071318508-1cdbab80d002",
        alt: "Pizza Cinque Formaggi aux cinq fromages fondants",
        tags: ["vegetarien", "sansViande"],
      },
      {
        name: "Chef",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, merguez de bœuf, œuf frais.",
        price: "17,80 €",
        photo: "photo-1579751626657-72bc17010498",
        alt: "Pizza Chef à la merguez de bœuf et à l'œuf frais",
      },
      {
        name: "Pizza Al Tonno",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, thon à l'huile, œuf frais, câpres, olives, poivrons marinés.",
        price: "17,10 €",
        photo: "photo-1609159085964-e47ec0804449",
        alt: "Pizza al Tonno au thon, câpres et olives",
        tags: ["sansViande"],
      },
      {
        name: "Pizza Al Prosciutto Di Parma",
        description:
          "Sauce tomate parfumée aux herbes, mozzarella, tranches de jambon de Parme sélectionné, œuf frais.",
        price: "19,90 €",
        photo: "photo-1762922425226-9411194abb68",
        alt: "Pizza au jambon de Parme finement tranché",
      },
    ],
  },
  {
    id: "bianche",
    icon: "🧀",
    label: "Pizzas Blanches & Spéciales",
    short: "Bianche",
    tagline:
      "Base crème fraîche, sans sauce tomate. Nos créations les plus généreuses.",
    dishes: [
      {
        name: "Pizza Pollo",
        description:
          "Base crème fraîche, poulet rôti halal, champignons, roquette fraîche.",
        price: "16,80 €",
        photo: "photo-1670952606267-f8389525e83b",
        alt: "Pizza Pollo au poulet rôti, champignons et roquette",
        badges: ["Poulet Halal"],
      },
      {
        name: "Josepha",
        description:
          "Base crème fraîche, fromage de chèvre, miel, noix, échalotes.",
        price: "17,80 €",
        photo: "photo-1617470702892-e01504297e84",
        alt: "Pizza Josepha au chèvre, miel et noix",
        tags: ["vegetarien", "sansViande"],
      },
      {
        name: "Pizza Salmone",
        description:
          "Base crème fraîche, mozzarella, saumon fumé d'exception.",
        price: "19,90 €",
        photo: "photo-1682264788192-9abdec90c425",
        alt: "Pizza Salmone au saumon fumé sur base crème fraîche",
        tags: ["sansViande"],
      },
      {
        name: "Pizza À La Crème De Truffe",
        description:
          "Base crème fraîche truffée (crema di tartufo), prosciutto di Parma et roquette fraîche.",
        price: "22,80 €",
        photo: "photo-1781722758472-581b018af1a8",
        alt: "Pizza à la crème de truffe, prosciutto di Parma et roquette",
        badges: ["Prestige"],
        tags: ["prestige"],
      },
      {
        name: "Pizza Cinquecento",
        description:
          "Création généreuse sucrée-salée. Pâte fourrée à la ricotta, base crème fraîche, roquette, tomates cerises, figues fraîches, jambon de Parme et Burrata entière.",
        price: "22,80 €",
        photo: "photo-1761138784773-5f5af9bce93d",
        alt: "Pizza Cinquecento à la burrata entière, figues et jambon de Parme",
        badges: ["Signature Maison"],
        tags: ["prestige"],
      },
    ],
  },
  {
    id: "entrees",
    icon: "🥗",
    label: "Entrées & Salades",
    short: "Entrées",
    tagline: "Nos entrées et salades gourmandes.",
    dishes: [
      {
        name: "Farandola Di Salumi Italiani",
        description:
          "Coppa, prosciutto di Parma, salame, pancetta, mortadella.",
        price: "16,50 €",
        photo: "photo-1704361190922-3e0f69266768",
        alt: "Planche de charcuteries italiennes : coppa, prosciutto, salame, pancetta, mortadella",
      },
      {
        name: "Carpaccio Di Manzo, Rucola, Parmesan",
        description: "Carpaccio de bœuf, roquette et parmesan.",
        price: "16,80 €",
        photo: "photo-1769638913675-49d0fc2ca54c",
        alt: "Carpaccio de bœuf finement tranché, roquette et copeaux de parmesan",
      },
      {
        name: "Soupe À L'oignon",
        description: "Zuppa di cipolla, soupe à l'oignon gratinée.",
        price: "10,80 €",
        photo: "photo-1768295982465-ffc2f48e63f4",
        alt: "Soupe à l'oignon gratinée servie en bol",
      },
      {
        name: "12 Escargots « Façon Bourgogne »",
        description:
          "12 lumache alla borgognona, con burro, aglio e prezzemolo — beurre, ail et persil.",
        price: "17,80 €",
      },
      {
        name: "Insalata Cesar",
        description:
          "Pollo, crostini all'aglio, uova dure, parmigiano, pomodorini, salsa cesare.",
        price: "17,80 €",
        photo: "photo-1746211108786-ca20c8f80ecd",
        alt: "Salade César au poulet, croûtons à l'ail et copeaux de parmesan",
      },
      {
        name: "Insalata Caprese",
        description:
          "Mozzarella di bufala, pomodorini, rucola, oliva, basilico.",
        price: "16,80 €",
        photo: "photo-1769458313937-b5ad8f84942e",
        alt: "Salade caprese : mozzarella di bufala, tomates cerises et basilic frais",
        tags: ["vegetarien", "sansViande"],
      },
      {
        name: "Insalata Giordino",
        description: "Farandola di verdure — farandole de légumes de saison.",
        price: "19,80 €",
        photo: "photo-1540420773420-3366772f4999",
        alt: "Salade de légumes frais de saison",
        tags: ["vegetarien", "sansViande"],
      },
      {
        name: "Insalata Del Nord E Sud",
        description: "Insalata, pomodoro, salmone affumicato e avocado.",
        price: "21,50 €",
        photo: "photo-1778104682746-546b832a6469",
        alt: "Salade au saumon fumé, avocat et jeunes pousses",
        tags: ["sansViande"],
      },
      {
        name: "Insalata « Cinquecento »",
        description: "Burrata, farandola di verdure — burrata et farandole de légumes.",
        price: "23,50 €",
        photo: "photo-1751451985575-c8c15d25694c",
        alt: "Salade Cinquecento : burrata crémeuse et farandole de légumes",
        badges: ["Signature Maison"],
        tags: ["vegetarien", "sansViande"],
      },
    ],
  },
  {
    id: "aperitif",
    icon: "🥂",
    label: "À Partager",
    short: "Apéritif",
    tagline: "À partager à l'apéritif.",
    dishes: [
      {
        name: "Farandole De Charcuterie Italienne",
        description:
          "Coppa, prosciutto di Parma, salame, pancetta, mortadella.",
        price: "16,50 €",
        photo: "photo-1704361190922-3e0f69266768",
        alt: "Planche de charcuteries italiennes à partager",
      },
      {
        name: "Boule De Burrata Et Tomates Cerise",
        description: "Burrata crémeuse, tomates cerises, basilic, huile d'olive.",
        price: "17,80 €",
        photo: "photo-1760023570385-ee484f7076b3",
        alt: "Boule de burrata entière et tomates cerises",
        tags: ["vegetarien", "sansViande"],
      },
    ],
  },
];

export const PHILOSOPHY = [
  {
    icon: "⏳",
    title: "Pâte Maturée 48h",
    text: "Farine italienne type 00, levain naturel, 48 heures de maturation lente à froid. Résultat : une pizza digeste, légère, à la corniche alvéolée et croustillante.",
    photo: "photo-1537734796389-e1fc293cf856",
    alt: "Les mains du pizzaiolo travaillant la pâte maturée",
  },
  {
    icon: "🇮🇹",
    title: "Produits d'Exception",
    text: "Prosciutto di Parma, mozzarella fior di latte, gorgonzola, crema di tartufo, burrata des Pouilles. Sélectionnés chez de petits producteurs italiens.",
    photo: "photo-1638981367648-d4909c40ab16",
    alt: "Pizzas napolitaines alignées sur des planches en bois",
  },
  {
    icon: "🔥",
    title: "Cuisson Traditionnelle",
    text: "Four napolitain monté à très haute température. Quatre-vingt-dix secondes de cuisson, pas une de plus — la signature de la vraie pizza verace.",
    photo: "photo-1706011465964-7a226eea129a",
    alt: "Flammes dans le four à bois napolitain en brique",
  },
];

export const GALLERY = [
  {
    photo: "photo-1671510113581-734a8f2ce5ad",
    alt: "La salle de Cinquecento et son mur de briques",
  },
  {
    photo: "photo-1786034760041-7dedbcf036bb",
    alt: "Clients attablés dans l'ambiance tamisée du restaurant",
  },
  {
    photo: "photo-1651978595416-9956a5fb8f7a",
    alt: "Une part de pizza fumante attrapée à table",
  },
  {
    photo: "photo-1574761136173-07e9d49b2a6f",
    alt: "Suspensions chaleureuses au-dessus des tables",
  },
];

export const HERO_SLIDES = [
  {
    photo: "photo-1622880833523-7cf1c0bd4296",
    alt: "Pizza en cuisson dans le four à bois, mozzarella fondante",
  },
  {
    photo: "photo-1570946581013-83b974ed3fb7",
    alt: "Le pizzaiolo enfournant une pizza à la pelle",
  },
  {
    photo: "photo-1598023696416-0193a0bcd302",
    alt: "Margherita au basilic frais et à la corniche alvéolée",
  },
];

export type OpeningHours = { day: string; time: string; closed?: boolean };

/** Typé explicitement : sans annotation, TS n'autoriserait pas `h.closed`
 *  puisque seule la ligne "Lundi" porte cette propriété. */
const HOURS: OpeningHours[] = [
  { day: "Lundi", time: "Fermé", closed: true },
  { day: "Mardi — Jeudi", time: "12h00 – 14h30  ·  19h00 – 22h30" },
  { day: "Vendredi — Samedi", time: "12h00 – 14h30  ·  19h00 – 23h30" },
  { day: "Dimanche", time: "19h00 – 22h30" },
];

export const INFO = {
  name: "Cinquecento Pizza",
  phone: "01 42 00 50 00",
  phoneHref: "tel:+33142005000",
  email: "ciao@cinquecento-pizza.fr",
  address: "Quartier des Halles",
  city: "75001 Paris",
  maps:
    "https://www.google.com/maps/search/?api=1&query=Cinquecento+Pizza%2C+Les+Halles%2C+75001+Paris",
  instagram: "https://www.instagram.com/cinquecento_pizza/",
  instagramHandle: "@cinquecento_pizza",
  tiktok: "https://www.tiktok.com/@cinquecento_pizza",
  hours: HOURS,
};
